import * as React from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'
import { Icon } from '@/design-system/atoms/Icon'
import { IconButton } from '@/design-system/atoms/IconButton'

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full'

const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[95vw] max-h-[95vh]',
}

export interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: React.ReactNode
  description?: React.ReactNode
  children: React.ReactNode
  footer?: React.ReactNode
  size?: ModalSize
  showCloseButton?: boolean
  closeOnBackdropClick?: boolean
  closeOnEscape?: boolean
  className?: string
  contentClassName?: string
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  className,
  contentClassName,
}) => {
  const ref = React.useRef<HTMLDivElement>(null)
  const titleId = React.useId()
  const descId = React.useId()

  React.useEffect(() => {
    if (!isOpen || !closeOnEscape) return
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [isOpen, closeOnEscape, onClose])

  React.useEffect(() => {
    if (!isOpen) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [isOpen])

  const FOCUSABLE =
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

  /**
   * Devolve o foco a quem abriu o diálogo. Sem isto, confirmar (ou cancelar)
   * desmonta o modal e o foco cai no `<body>`: quem usa teclado volta ao topo da
   * página e perde o lugar na árvore/lista de onde veio.
   *
   * A captura vive num ref preenchido no PRIMEIRO efeito, antes de qualquer
   * movimentação de foco. Capturar dentro de um efeito declarado depois do
   * auto-foco guardaria um elemento de DENTRO do modal — que no cleanup já está
   * desmontado, e `focus()` num nó destacado não faz nada.
   */
  const focoAnterior = React.useRef<HTMLElement | null>(null)

  React.useEffect(() => {
    if (!isOpen) return
    focoAnterior.current = document.activeElement as HTMLElement | null
    return () => {
      const alvo = focoAnterior.current
      focoAnterior.current = null
      // `isConnected`: se quem abriu saiu do DOM junto, não há para onde voltar.
      if (alvo?.isConnected) alvo.focus()
    }
  }, [isOpen])

  React.useEffect(() => {
    if (!isOpen || !ref.current) return
    ref.current.querySelector<HTMLElement>(FOCUSABLE)?.focus()
  }, [isOpen])

  /**
   * Armadilha de foco: `aria-modal` promete ao leitor de tela que o resto da
   * página não existe; sem o trap o Tab passeia pelo conteúdo atrás e a promessa
   * vira mentira.
   */
  const handleTabTrap = (event: React.KeyboardEvent) => {
    if (event.key !== 'Tab' || !ref.current) return
    const focusables = [
      ...ref.current.querySelectorAll<HTMLElement>(FOCUSABLE),
    ].filter(
      el =>
        !el.hasAttribute('hidden') && el.getAttribute('aria-hidden') !== 'true'
    )
    if (focusables.length === 0) return
    const primeiro = focusables[0]
    const ultimo = focusables[focusables.length - 1]
    if (event.shiftKey && document.activeElement === primeiro) {
      event.preventDefault()
      ultimo.focus()
    } else if (!event.shiftKey && document.activeElement === ultimo) {
      event.preventDefault()
      primeiro.focus()
    }
  }

  if (!isOpen) return null

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-[9999] flex items-center justify-center overflow-y-auto p-4',
        className
      )}
    >
      <div
        className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm dark:bg-gray-950/70"
        onClick={closeOnBackdropClick ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        onKeyDown={handleTabTrap}
        className={cn(
          'relative flex max-h-[calc(100vh-2rem)] w-full flex-col rounded-xl bg-white shadow-xl dark:bg-gray-900',
          sizeClasses[size],
          contentClassName
        )}
      >
        {(title || showCloseButton) && (
          <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <div className="flex-1">
              {title && (
                <h2
                  id={titleId}
                  className="text-lg font-semibold leading-tight"
                >
                  {title}
                </h2>
              )}
              {description && (
                <p
                  id={descId}
                  className="mt-1 text-sm text-gray-500 dark:text-gray-400"
                >
                  {description}
                </p>
              )}
            </div>
            {showCloseButton && (
              <IconButton
                variant="ghost"
                size="sm"
                icon={<Icon name="x" size={20} />}
                aria-label="Fechar modal"
                onClick={onClose}
              />
            )}
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}

Modal.displayName = 'Modal'
